import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BookOpen, ArrowLeft } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import Layout from '@/components/Layout';
import { LIBRARY } from '@/constants/testIds';

export default function Library() {
	const { slug } = useParams();
	const [items, setItems] = useState([]);
	const [active, setActive] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get('/education');
				setItems(data.items || []);
			} catch (err) {
				toast.error(formatApiError(err));
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	useEffect(() => {
		if (!slug) {
			setActive(null);
			return;
		}
		(async () => {
			try {
				const { data } = await api.get(`/education/${slug}`);
				setActive(data);
			} catch (err) {
				toast.error(formatApiError(err));
			}
		})();
	}, [slug]);

	const groups = items.reduce((acc, it) => {
		acc[it.topic] = acc[it.topic] || [];
		acc[it.topic].push(it);
		return acc;
	}, {});

	if (slug && active) {
		return (
			<Layout>
				<div data-testid={LIBRARY.root} className="max-w-3xl mx-auto">
					<Link to="/library" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline">
						<ArrowLeft className="w-4 h-4" /> Back to library
					</Link>
					<article className="card-soft p-8 mt-4">
						<div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">{active.topic}</div>
						<h1 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight text-slate-900">{active.title}</h1>
						<p className="mt-3 text-slate-600 leading-relaxed">{active.summary}</p>
						<div className="mt-6 prose max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
							{active.body}
						</div>
						<div className="mt-10 px-5 py-4 rounded-2xl bg-emerald-50 text-emerald-800 text-sm border border-emerald-100">
							MyFamShield is a Family Preparedness Platform. We educate and coach — we never sell products.
						</div>
					</article>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div data-testid={LIBRARY.root}>
				<h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-slate-900">Family Library</h1>
				<p className="mt-2 text-slate-600">Beautiful, simple lessons to help your family grow more resilient.</p>

				{loading && <div className="mt-10 text-slate-500">Loading…</div>}

				<div className="mt-8 space-y-10">
					{Object.entries(groups).map(([topic, list]) => (
						<section key={topic}>
							<div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">{topic}</div>
							<div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
								{list.map((it) => (
									<Link
										to={`/library/${it.slug}`}
										key={it.slug}
										data-testid={LIBRARY.card}
										className="card-soft p-6 hover:-translate-y-0.5 transition-transform block group"
									>
										<div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-4">
											<BookOpen className="w-5 h-5" />
										</div>
										<h3 className="font-heading text-lg font-semibold text-slate-900 leading-snug">{it.title}</h3>
										<p className="mt-2 text-sm text-slate-600 line-clamp-3">{it.summary}</p>
										<div className="mt-4 text-sm font-semibold text-emerald-700 group-hover:underline">Read article →</div>
									</Link>
								))}
							</div>
						</section>
					))}
				</div>
			</div>
		</Layout>
	);
}
